import React from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./Popover";
import { X } from "lucide-react";
import { Button } from "./Button";
import { useNavigate } from "react-router-dom";

export type PlanLimitType =
  | "listing"
  | "campaign"
  | "post"
  | "postSelection"
  | "storage"
  | "listingStorage"
  | "zapcap"
  | "activeListingLimit";

interface PlanLimitReachedProps {
  open: boolean;
  onClose: () => void;
  type: PlanLimitType;
  // When the backend named the actual limit (e.g. a 410 message), show that
  // instead of the canned first line.
  overrideMessage?: string;
}

const messageMaker = (type: PlanLimitType): { m1: string; m2: string } => {
  switch (type) {
    case "listing":
      return {
        m1: "You've reached the plans listing limit for the month and cannot create a new listing. Limits reset on the first day of every month.",
        m2: "To create additional listings, please upgrade your plan by contacting support.",
      };
    case "campaign":
      return {
        m1: "You've reached the plans post limit for the month and cannot create a new campaigns or posts. Limits reset on the first day of every month.",
        m2: "To create additional campaigns or posts, please upgrade your plan by contacting support.",
      };
    case "post":
      return {
        m1: "You've reached the plans post limit for the month and cannot create a new posts. Limits reset on the first day of every month.",
        m2: "To create additional posts, please upgrade your plan by contacting support.",
      };
    case "postSelection":
      return {
        m1: "Your plan doesn't have enough posts left this month for the platforms you selected. Each platform counts as one post, and limits reset on the first day of every month.",
        m2: "Deselect some platforms to continue, or upgrade your plan by contacting support.",
      };
    case "storage":
      return {
        m1: "You've reached the plans storage/transfer limit for the month and cannot upload any new files. Limits reset on the first day of every month.",
        m2: "To upload additional files, please upgrade your plan by contacting support.",
      };
    case "listingStorage":
      return {
        m1: "You've reached the plans storage/transfer limit for the month and cannot upload any new files or create new listings. Limits reset on the first day of every month.",
        m2: "To upload additional files or create new listings, please upgrade your plan by contacting support.",
      };
    case "zapcap":
      return {
        m1: "You've reached the plans captioned-video limit for the month and cannot caption any more videos. Limits reset on the first day of every month.",
        m2: "To caption more videos, please upgrade your plan by contacting support.",
      };
    case "activeListingLimit":
      return {
        m1: "You've reached the active listing limit for your plan and cannot create a new campaign for this listing.",
        m2: "To create campaigns for additional active listings, please upgrade your plan by contacting support.",
      };
    default:
      return {
        m1: "You've reached a plan limit and cannot continue with this action.",
        m2: "To continue, please upgrade your plan by contacting support.",
      };
  }
};

const PlanLimitReached = ({ open, onClose, type, overrideMessage }: PlanLimitReachedProps) => {
  const navigate = useNavigate();
  const { m1, m2 } = messageMaker(type);
  const primaryMessage = overrideMessage || m1;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-40 flex items-center justify-center">
      <Popover
        open={open}
        onOpenChange={(open) => {
          if (!open) {
            onClose();
          }
        }}
      >
        {/* Centered trigger for positioning */}
        <PopoverTrigger asChild>
          <button
            type="button"
            className="pointer-events-none fixed top-[40%] "
            aria-hidden
          />
        </PopoverTrigger>
        <PopoverContent
          portalled={false}
          sideOffset={0}
          align="center"
          className="w-[28rem] max-w-[90vw] bg-white border border-gray-300 rounded-lg p-6 shadow-xl text-primary z-50"
        >
          <h2 className="text-xl font-bold mb-4 text-primary text-center">
            Plan Limit Reached
          </h2>
          <button
            className="absolute top-4 right-4 text-gray-500 hover:text-red-500"
            onClick={onClose}
          >
            <X className="h-6 w-6" />
          </button>
          <div className="space-y-4">
            <p className="text-gray-700 text-sm">{primaryMessage}</p>
            <p className="text-gray-700 text-sm">{m2}</p>
          </div>
          <div className="flex justify-center mt-4">
            <Button
              onClick={() => {
                navigate("/help", {
                  state: { tab: "contact" },
                });
              }}
            >
              Contact Support
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default PlanLimitReached;
